"use client";

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { CopySimpleIcon, TrashIcon, CheckIcon, FrameCornersIcon } from "@phosphor-icons/react";
import { useOptionalToolsSharedCaseData, stripWebpToRawBase64, MAX_CAPTURE_BASE64_CHARS, MAX_CAPTURES, type ToolsSharedCaptureEntry } from "./tools-shared-case-data";
import { useToolsDrawer } from "./tools-drawer";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);
export function webpEntryToDataUrl(entry: ToolsSharedCaptureEntry): string {
  return `data:image/webp;base64,${entry.webpBase64}`;
}

/** Parse pasted text into raw base64; returns null if empty or too large. */
export function parseWebpCaptureInput(text: string): string | null {
  const raw = stripWebpToRawBase64(text);
  if (!raw) return null;
  if (raw.length > MAX_CAPTURE_BASE64_CHARS) return null;
  return raw;
}
export function useCapturesState(caseId: string | undefined) {
  const remote = useOptionalToolsSharedCaseData();
  const apiActive = Boolean(caseId && remote && remote.caseId === caseId);
  const setCaptures = apiActive ? remote!.setCaptures : null;
  const captures = apiActive ? remote!.hydrated ? remote!.captures : [] : [];
  const addCapture = useCallback((webpBase64: string) => {
    if (!setCaptures) return false;
    const raw = stripWebpToRawBase64(webpBase64);
    if (!raw || raw.length > MAX_CAPTURE_BASE64_CHARS) return false;
    setCaptures(prev => {
      const entry: ToolsSharedCaptureEntry = {
        id: crypto.randomUUID(),
        webpBase64: raw,
        createdAt: Date.now()
      };
      return [entry, ...prev].slice(0, MAX_CAPTURES);
    });
    return true;
  }, [setCaptures]);
  const removeCapture = useCallback((id: string) => {
    if (!setCaptures) return;
    setCaptures(prev => prev.filter(c => c.id !== id));
  }, [setCaptures]);
  const remoteRefetch = remote?.refetch;
  const refetch = useCallback(() => {
    if (apiActive && remoteRefetch) {
      void remoteRefetch();
    }
  }, [apiActive, remoteRefetch]);
  return useMemo(() => ({
    captures,
    addCapture,
    removeCapture,
    refetch,
    /** True when captures load/save via `/api/protected/sharedData` for the open case. */
    apiActive
  }), [captures, addCapture, removeCapture, refetch, apiActive]);
}
export interface ToolsCapturesContentProps {
  captures: ToolsSharedCaptureEntry[];
  onAddCapture: (webpBase64: string) => boolean;
  onRemoveCapture: (id: string) => void;
  /** When false, list is empty and changes are not persisted (no open case). */
  apiActive: boolean;
}
export function ToolsCapturesContent({
  captures,
  onAddCapture,
  onRemoveCapture,
  apiActive
}: ToolsCapturesContentProps) {
  const toolsDrawer = useToolsDrawer();
  const applyCaptureToEventMedia = toolsDrawer?.applyCaptureToEventMedia;
  const eventMediaCaptureApplyReady = toolsDrawer?.eventMediaCaptureApplyReady ?? false;
  const [draft, setDraft] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [applyingToEventId, setApplyingToEventId] = useState<string | null>(null);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const applyingToEventTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleAdd = useCallback(() => {
    setAddError(null);
    if (!apiActive) {
      setAddError("Open a case to save captures to the server.");
      return;
    }
    const parsed = parseWebpCaptureInput(draft);
    if (!parsed) {
      setAddError(draft.trim() ? "Invalid or too large (max ~200KB image as base64)." : "Paste a WebP base64 string or data URL.");
      return;
    }
    const ok = onAddCapture(parsed);
    if (ok) setDraft("");else setAddError("Could not add capture.");
  }, [draft, onAddCapture, apiActive]);
  const copyDataUrl = useCallback((entry: ToolsSharedCaptureEntry) => {
    const url = webpEntryToDataUrl(entry);
    navigator.clipboard.writeText(url).then(() => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      setCopiedId(entry.id);
      copiedTimeoutRef.current = setTimeout(() => {
        setCopiedId(null);
        copiedTimeoutRef.current = null;
      }, 1500);
    });
  }, []);
  const copyRawBase64 = useCallback((entry: ToolsSharedCaptureEntry) => {
    navigator.clipboard.writeText(entry.webpBase64).then(() => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      setCopiedId(`${entry.id}-raw`);
      copiedTimeoutRef.current = setTimeout(() => {
        setCopiedId(null);
        copiedTimeoutRef.current = null;
      }, 1500);
    });
  }, []);
  const applyToEventMedia = useCallback((entry: ToolsSharedCaptureEntry) => {
    if (!applyCaptureToEventMedia || !eventMediaCaptureApplyReady) return;
    const dataUrl = webpEntryToDataUrl(entry);
    const ok = applyCaptureToEventMedia(dataUrl);
    if (!ok) return;
    if (applyingToEventTimeoutRef.current) clearTimeout(applyingToEventTimeoutRef.current);
    setApplyingToEventId(entry.id);
    applyingToEventTimeoutRef.current = setTimeout(() => {
      setApplyingToEventId(null);
      applyingToEventTimeoutRef.current = null;
    }, 1000);
  }, [applyCaptureToEventMedia, eventMediaCaptureApplyReady]);
  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      if (applyingToEventTimeoutRef.current) clearTimeout(applyingToEventTimeoutRef.current);
    };
  }, []);
  return <div className="flex flex-1 min-h-0 flex-col overflow-hidden" data-sentry-component="ToolsCapturesContent" data-sentry-source-file="tools-captures.tsx">
      <div className="shrink-0 px-3 py-1 border-b border-white/20">
        {!apiActive ? <p className="text-amber-400/90 text-[11px]" role="status">
            Select a case to load and save captures.
          </p> : null}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {captures.length === 0 ? <p className="px-3 py-4 text-white/50 text-xs">
            {apiActive ? "No captures yet." : "No case open — captures are not loaded."}
          </p> : <ul className="flex flex-col gap-0 list-none pl-0">
            {captures.map(entry => <li key={entry.id} className="flex flex-col gap-2 border-b border-white/10 px-3 py-1 sm:flex-row sm:items-start">
                <div className="shrink-0 rounded border border-white/10 overflow-hidden bg-black/40 w-full max-w-[200px] aspect-video flex items-center justify-center">
                  <img src={webpEntryToDataUrl(entry)} alt="" className="max-w-full max-h-[120px] w-auto h-auto object-contain" />
                </div>
                <div className="min-w-0 flex-1 flex flex-col gap-1">
                  <p className="text-white/50 text-[11px]">
                    {dayjs(entry.createdAt).fromNow()} ·{" "}
                    <span className="text-white/40 font-mono text-[10px]">
                      {(entry.webpBase64.length / 1024).toFixed(0)} KB b64
                    </span>
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {copiedId === entry.id ? <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-white/70">
                        <CheckIcon size={14} weight="bold" />
                        Copied URL
                      </span> : <button type="button" onClick={() => copyDataUrl(entry)} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-white/70 hover:bg-white/10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/30">
                        <CopySimpleIcon size={14} weight="duotone" />
                        Copy data URL
                      </button>}
                    {copiedId === `${entry.id}-raw` ? <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-white/70">
                        <CheckIcon size={14} weight="bold" />
                        Copied base64
                      </span> : <button type="button" onClick={() => copyRawBase64(entry)} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-white/60 hover:bg-white/10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/30">
                        <CopySimpleIcon size={14} weight="duotone" />
                        Copy base64
                      </button>}
                    <button type="button" disabled={!eventMediaCaptureApplyReady} title={eventMediaCaptureApplyReady ? "Add or update the captured image preview for the current event media item" : "Open an event for editing with media to apply this capture"} onClick={() => applyToEventMedia(entry)} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-white/70 hover:bg-white/10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/30 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent">
                      <span className="relative inline-flex items-center">
                        <FrameCornersIcon size={14} weight="duotone" className={applyingToEventId === entry.id ? "text-green-500" : "text-white/70"} />
                        <span className="absolute -right-1 -top-0.5 text-[9px] text-white/70 leading-none">
                          +
                        </span>
                      </span>
                      To event media
                    </button>
                    <button type="button" onClick={() => onRemoveCapture(entry.id)} className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-white/60 hover:bg-red-500/20 hover:text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/30 ml-auto sm:ml-0" aria-label="Remove capture">
                      <TrashIcon size={14} weight="duotone" />
                      Remove
                    </button>
                  </div>
                </div>
              </li>)}
          </ul>}
      </div>
    </div>;
}